import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import { ArrowUpRight } from "lucide-react";

// Compact date range for an in-house exhibition Event (uses starts_at / ends_at).
const formatExhibitionWhen = (ex) => {
  const s = ex.starts_at ? new Date(ex.starts_at) : null;
  const e = ex.ends_at ? new Date(ex.ends_at) : null;
  if (!s) return ex.recurrence_label || "";
  const short = { day: "numeric", month: "short" };
  const full = { day: "numeric", month: "short", year: "numeric" };
  if (!e || s.toDateString() === e.toDateString()) {
    return s.toLocaleDateString("en-IN", full);
  }
  const sameYear = s.getFullYear() === e.getFullYear();
  return `${s.toLocaleDateString("en-IN", sameYear ? short : full)} → ${e.toLocaleDateString("en-IN", full)}`;
};

const GALLERY_IMGS = [
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/od25b75l_506397686_1130193119128821_4442618323425677955_n.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/t4zau7nq_506353663_1130193165795483_6401457337659159852_n.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/mdh8z899_506944384_1130184962462970_3540602056786253828_n.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/k3d67uqm_506493707_1130195782461888_8770169307549597322_n.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/3tk2fnso_506270965_1130195725795227_8753193179305175668_n.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/6sfx82wp_506348207_1130184392463027_7209124629313855707_n.jpg",
];

export default function Gallery() {
  const [events, setEvents] = useState([]);
  const [ownExhibitions, setOwnExhibitions] = useState([]);
  useEffect(() => {
    api.get("/events", { params: { section: "art-gallery" } })
      .then(r => {
        setEvents(r.data);
        setOwnExhibitions(r.data.filter(e => e.category === "exhibition"));
      }).catch(() => {});
  }, []);

  // Split in-house exhibitions by today's date: currently on view vs upcoming.
  // For legs missing ends_at we treat the exhibition as a single-day event.
  const { nowShowing, upcomingOwn } = useMemo(() => {
    const now = new Date();
    // Normalise "today" to start-of-day so an event ending today still counts as "on view".
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000 - 1);
    const showing = [];
    const upcoming = [];
    for (const ex of ownExhibitions) {
      const s = ex.starts_at ? new Date(ex.starts_at) : null;
      const e = ex.ends_at ? new Date(ex.ends_at) : s;
      if (!s) continue;
      if (s <= endOfToday && (e || s) >= startOfToday) {
        showing.push(ex);
      } else if (s > endOfToday) {
        upcoming.push(ex);
      }
      // past exhibitions (e < startOfToday) drop off — archive not shown here.
    }
    upcoming.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
    return { nowShowing: showing, upcomingOwn: upcoming };
  }, [ownExhibitions]);

  return (
    <div data-testid="gallery-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="overline">01 — Art Gallery</div>
            <h1 className="display text-6xl md:text-8xl mt-6">A room <em className="italic text-terra">for looking.</em></h1>
            <p className="mt-8 text-base text-muted2 max-w-xl leading-relaxed">
              Carpe Diem Art Gallery hosts curated solo and group exhibitions throughout the year — paintings,
              sculpture, photography, mixed media and the occasional moving-image work. We are home for many
              of Goa&apos;s artists and a host for travelling practitioners.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/events?section=art-gallery" className="btn-primary" data-testid="gallery-view-exhibitions">View exhibitions</Link>
              <Link to="/gallery/apply" className="btn-ghost" data-testid="gallery-propose">Apply to exhibit</Link>
              <Link to="/gallery/buy" className="btn-ghost" data-testid="gallery-buy">Buy art</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] overflow-hidden border border-soft">
              <img src={GALLERY_IMGS[0]} alt="Gallery main" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>

      {/* Currently showing — in-house exhibitions whose date range covers today */}
      {nowShowing.length > 0 && (
        <section className="bg-cream-2" data-testid="currently-showing">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24">
            <div className="overline">Currently showing</div>
            <h2 className="display text-4xl md:text-5xl mt-4">On view now.</h2>
            <div className="mt-12 space-y-16">
              {nowShowing.map((ex, i) => (
                <Link key={ex.id} to={`/events/${ex.slug}`} className="group grid lg:grid-cols-12 gap-8 items-center" data-testid={`current-exhibition-${ex.slug}`}>
                  <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <div className="aspect-[16/10] overflow-hidden border border-soft">
                      <img src={ex.image_url} alt={ex.title} loading="lazy" className="reveal-img w-full h-full object-cover"/>
                    </div>
                  </div>
                  <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <div className="overline">{formatExhibitionWhen(ex)}</div>
                    <h3 className="display text-4xl md:text-5xl mt-4">{ex.title}</h3>
                    {ex.short_description && <p className="mt-5 text-base text-muted2 leading-relaxed max-w-md">{ex.short_description}</p>}
                    <div className="mt-6 text-xs inline-flex items-center gap-1 link-hover">
                      Explore this exhibition <ArrowUpRight size={14}/>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming in-house exhibitions — added by admin, starting in the future */}
      {upcomingOwn.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24" data-testid="upcoming-own-exhibitions">
          <div className="overline">Coming up</div>
          <h2 className="display text-4xl md:text-5xl mt-4">Upcoming at the gallery.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {upcomingOwn.map(ex => <EventCard key={ex.id} event={ex} testIdPrefix="upcoming-own" />)}
          </div>
        </section>
      )}

      {/* Asymmetric masonry */}
      <section className="bg-cream-2 py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline mb-3">Recent shows</div>
          <h2 className="display text-4xl md:text-5xl">A few from the archive.</h2>
          <div className="mt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-7 aspect-[4/3] overflow-hidden border border-soft">
              <img src={GALLERY_IMGS[1]} className="w-full h-full object-cover" alt="archive-1"/>
            </div>
            <div className="col-span-12 md:col-span-5 aspect-[3/4] overflow-hidden border border-soft md:translate-y-12">
              <img src={GALLERY_IMGS[2]} className="w-full h-full object-cover" alt="archive-2"/>
            </div>
            <div className="col-span-12 md:col-span-4 aspect-square overflow-hidden border border-soft md:-translate-y-6">
              <img src={GALLERY_IMGS[3]} className="w-full h-full object-cover" alt="archive-3"/>
            </div>
            <div className="col-span-12 md:col-span-8 aspect-[16/9] overflow-hidden border border-soft">
              <img src={GALLERY_IMGS[4]} className="w-full h-full object-cover" alt="archive-4"/>
            </div>
          </div>
        </div>
      </section>

      {events.filter(e => e.category !== "exhibition").length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="overline">Also at the gallery</div>
          <h2 className="display text-4xl md:text-5xl mt-4">Talks, openings &amp; walk-throughs.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {events.filter(e => e.category !== "exhibition").map(e => <EventCard key={e.id} event={e} />)}
          </div>
        </section>
      )}
    </div>
  );
}
